/**
 * 기사편집 v2 — 액션(모달·ajax) 모음.
 *
 * v1 은 이 동작들을 `public/js/a-member-edit.js` 의 전역함수 13개로 갖고 있었다
 * (givePoint·cach_inout·regPayAtid·resetGoogleToken·openPushTestModal …).
 * v1 은 동결이므로 그 파일을 v2 가 로드할 수 없다 → 같은 엔드포인트를 쓰는 메서드로 옮긴다.
 *
 * 전역함수 중 **공용(common.js·$j.F.Admin.Util)에 있는 것은 그대로 재사용**한다:
 *   registerPhoneHandler · coNumber · decodeMemberBank · registerBonsaHandler ·
 *   updateBonsaSelectBox · isSosokSamsung · isSosokJbbuller · nvl · int ·
 *   $j.F.Admin.Util.userDocs · $j.F.Admin.Util.devilMobile
 *
 * 설계: .ai/domains/member-edit-v2.md §10
 */
import Util from '../../../Common/Util';

/**
 * 별창을 부모창 한가운데 띄우기 위한 [x, y, window.open 옵션문자열].
 * window.open 은 좌표를 못 받는 브라우저가 있어 띄운 뒤 moveTo 로 옮긴다.
 */
function centeredWindowOpts(ww, hh, canscroll, canresize) {
    const x = ((window.innerWidth  / 2) - (ww / 2)) + window.screenX;
    const y = ((window.innerHeight / 2) - (hh / 2)) + window.screenY;
    const opts = `toolbar=no,status=no,WIDTH=${ww},HEIGHT=${hh}, directories=no,`
               + `scrollbars=${canscroll},location=no,resizable=${canresize},menubar=no,location=no`;
    return [x, y, opts];
}

/** 계좌 3요소를 '|' 한 줄로. 구분자와 홑따옴표는 값에서 털어낸다 */
function encodeMemberBank(bank, account, actholder) {
    return [bank, account, actholder].map(v => String(v ?? '').replace(/['|]/g, '')).join('|');
}

export default class Actions {
    /** @param {object} page $j.Pages.Admin.MemberEdit 인스턴스 (state 공유) */
    constructor(page) {
        this.p = page;
    }

    get mb()   { return this.p.mb; }
    get mbNo() { return this.p.mb.num; }

    // ───────────────────────────────────────────────── 신원·계정

    /** 아이디 변경 모달 */
    idChange() {
        if (!this.mbNo || this.p.editMode === 'add') {
            $j.F.Admin.Util.tsalert('error', '아이디 변경 불가합니다.');
            return;
        }
        $j.Ui.JBModal.target('idchange-modal', {
            target: '$j.F.Admin.Member.IdChangeForm',
            data:   { mbNo: this.mbNo },
            title:  '아이디 변경',
            width:  '500px',
        });
    }

    /**
     * 비밀번호를 핸드폰 마지막 마디로 채운다 (v1 resetPass 와 동일).
     * 리셋일 때만 회원이 다음 접속 때 비밀번호를 새로 정하게 한다 — 칸에 직접 치면 그 표시를 푼다(MemberEdit 바인딩).
     */
    pwdReset() {
        const parts = String(Util.nvl(this.mb.mem_phone)).split('-');
        $('#me_mem_pwd').val(parts[2] || '');
        $('#me_pwd_reset').val(parts[2] ? '1' : '');
    }

    /** 아이디 중복검사 */
    checkId() {
        const id = $('#me_mem_id').val();
        if (String(id).length < 4) return;
        $.getJSON('/adm/ajax/member/check', { mb_no: this.mbNo, I: id }, (d) => {
            const dup = d.dup || '';
            dup.includes('I') ? $j.F.Admin.Util.tsalert('error', '이미 사용중인 아이디입니다.')
                              : $j.F.Admin.Util.tsalert('success', '사용가능한 아이디 입니다.');
        });
    }

    /**
     * 핸드폰 중복검사.
     *
     * 소유자번호(O)를 같이 보낸다 — 소속자가 소유자 번호를 그대로 쓰는 건 중복이 아니고,
     * 신규등록·소속자추가는 아직 회원번호가 없어 서버가 mb_no 로 소유자를 알 수 없다.
     */
    checkPhone() {
        const data = {
            mb_no: this.mbNo,
            O:     $('#meForm [name="mem_ownercode"]').val() || '',
            P:     $('#me_mem_phone').val(),
        };
        $.getJSON('/adm/ajax/member/check', data, (d) => {
            const dup = d.dup || '';
            dup.includes('P') ? $j.F.Admin.Util.tsalert('error', '이미 사용중인 핸드폰번호 입니다.')
                              : $j.F.Admin.Util.tsalert('success', '사용가능한 핸드폰번호 입니다.');
        });
    }

    /**
     * 사업자정보 편집 모달 — 열3 요약(사업자·상호·대표)을 누르면 열린다.
     *
     * **기존 회원은 모달의 [저장]으로 바로 저장한다** (/adm/ajax/company/save) — 기사/장비 모달처럼
     * 모달 안에서 끝난다. 신규등록은 아직 회원번호가 없어 [확인]으로 값만 남기고 하단 [저장]이 함께 싣는다.
     *
     * 입력칸은 폼(#meForm) 안 숨은 자리(#meCompanyHome)에 있다 — 신규등록은 폼 직렬화로 저장되고,
     * 기존 회원도 하단 [저장]이 같은 값을 다시 실어 보내므로 폼과 어긋나면 안 된다. 모달은 그 칸들을
     * **통째로 빌려 왔다가 닫힐 때 돌려놓는다** (append 는 이동이라 blur 검사·달력 바인딩이 따라온다).
     * 저장하지 않고 닫으면(취소·✕·바깥·Esc) 연 시점 값으로 되돌린다.
     */
    companyEdit() {
        const $fields = $('#meCompanyFields');
        if (!$fields.length) return;

        const $inputs = $fields.find('input[name]');
        const before  = $inputs.map((i, el) => el.value).get();
        const locked  = $inputs.first().prop('disabled');   // 탈퇴회원 잠금 — 보기만
        const direct  = this.p.editMode === 'edit';         // 바로 저장할 회원번호가 있는가
        let applied   = false;

        const okBtn = locked ? ''
            : `<button type="button" class="me-btn me-btn--accent" data-co="ok">${direct ? '저장' : '확인'}</button>`;
        const note  = (locked || direct) ? ''
            : '<p class="me-co-modal__note">신규등록은 하단 [저장]을 눌러야 함께 저장됩니다.</p>';

        const m = $j.Ui.Modal.open({
            id: 'member-company-modal', width: '360px', variant: 'admin',
            panelClass: 'me-co-modal',
            title: '사업자정보',
            html: `${note}
                   <div class="me-co-modal__foot">
                       <button type="button" class="me-btn" data-co="cancel">${locked ? '닫기' : '취소'}</button>
                       ${okBtn}
                   </div>`,
            onClose: () => {
                if (!applied) $inputs.each((i, el) => { el.value = before[i]; });
                $('#meCompanyHome').append($fields);
                this.companySummary();
            },
        });
        m.el.find('.ui-modal__body').prepend($fields);
        // 사업자번호는 000-00-00000 으로 보여준다 — 치는 중(registerCoNumberHandler)과 같은 모양.
        // 서버는 숫자만 남겨 저장하므로, 모양만 달라진 건 '변경됨'으로 치지 않는다 (companyDirty).
        if (!locked) {
            const $no = $fields.find('[name="co_number"]');
            $no.val(Util.coNumber($no.val()));
        }

        m.el.on('click', '[data-co]', (e) => {
            if ($(e.currentTarget).data('co') !== 'ok') { m.close(); return; }
            if (!direct) { applied = true; m.close(); return; }
            this._companySave($inputs, $(e.currentTarget), () => { applied = true; m.close(); });
        });
    }

    /** 사업자정보만 저장 — 성공하면 지금 값이 새 기준(defaultValue)이 된다 */
    _companySave($inputs, $btn, done) {
        const data = { mb_no: this.mbNo };
        $inputs.each((i, el) => { data[el.name] = el.value; });

        $btn.prop('disabled', true).text('저장중...');
        $.post('/adm/ajax/company/save', data, null, 'json')
            .done((d) => {
                if (!d.result) { $j.F.Admin.Util.tsalert('error', d.message); return; }
                $inputs.each((i, el) => { el.defaultValue = el.value; });
                $j.Ui.Toast.show(d.message, { type: 'success', place: 'center' });
                done();
            })
            .fail((xhr) => $j.F.Admin.Util.tsalert('error', `저장 중 오류가 발생했습니다. (${xhr.status})`))
            .always(() => $btn.prop('disabled', false).text('저장'));
    }

    /** 저장 성공 후 — 지금 값이 새 기준이다. '변경됨' 표시를 내린다 */
    companyCommit() {
        $('#meCompanyFields input[name]').each((i, el) => { el.defaultValue = el.value; });
        this.companySummary();
    }

    /**
     * 사업자정보를 고쳐 놓고 아직 [저장]하지 않았는가. 기준은 마지막으로 저장된 값(defaultValue).
     * 모달이 열려 있어도 같은 입력칸이라 그대로 잰다 — 창 닫기 경고(MemberEdit._guardUnload)도 이걸 본다.
     */
    companyDirty() {
        let dirty = false;
        $('#meCompanyFields input[name]').each((i, el) => {
            // 사업자번호는 하이픈 유무만 다르면 같은 값이다
            const norm = (v) => (el.name === 'co_number' ? v.replace(/\D/g, '') : v);
            if (norm(el.value) !== norm(el.defaultValue)) dirty = true;
        });
        return dirty;
    }

    /** 사업자정보 요약줄을 입력칸 값으로 다시 그린다. 저장 전 변경이 있으면 '변경됨' 표시 */
    companySummary() {
        const dirty = this.companyDirty();
        $('[data-co-sum]').each((i, el) => {
            const v = $(`#meCompanyFields [name="${el.dataset.coSum}"]`).val() || '';
            el.textContent = el.dataset.coSum === 'co_number' ? Util.coNumber(v) : v;   // 000-00-00000
        });
        $('#meCompanyDirty').prop('hidden', !dirty);
    }

    /** 사업자번호 중복검사 */
    checkCoNumber() {
        const v = $('#me_co_number').val();
        if (!v) return;
        $.getJSON('/adm/ajax/company/check', { co_number: v, mb_no: this.mb.mem_ownercode }, (d) => {
            d.result === true ? $j.F.Admin.Util.tsalert('success', '사용가능한 사업자번호 입니다.')
                              : $j.F.Admin.Util.tsalert('error', `사업자번호 중복 => ${d.mem_name}`);
        });
    }

    // ───────────────────────────────────────────────── 소속·소유자

    /** 소유자 검색 — 선택하면 hidden(mem_ownercode)과 표시필드를 함께 채운다 */
    findOwner() {
        if ($('#me_mem_permit').val() !== '소속자') {
            $j.F.Admin.Util.tsalert('error', '소속자만 소유자 코드가 필요합니다.');
            return;
        }
        $j.Ui.JBModal.target('find-member-modal', {
            target: '$j.F.Admin.Member.Find',
            data: {
                is_owner: true,
                onSelect: (num, text) => {
                    $('#me_mem_ownercode').val(text);
                    $('input[name=mem_ownercode]').val(num);
                },
            },
            title: '소유자 검색',
            width: '460px',
        });
    }

    /** 소개기사 검색 */
    findIntroducer() {
        $j.Ui.JBModal.target('find-member-modal', {
            target: '$j.F.Admin.Member.Find',
            data: {
                is_owner: false,
                onSelect: (num, text) => {
                    $('input[name=mem_introducer]').val(num);   // 저장에 실리는 값
                    this.p.introText = text;
                    this.p.drawIntro();                          // 이름 + 지급버튼이 이때 생긴다
                },
            },
            title: '소개기사 검색',
            width: '460px',
        });
    }

    /**
     * 소개기사 버튼을 누르면 뜨는 작은 모달 — 점수 지급 · 수정 · 해제를 한 곳에서.
     *
     * 예전에는 버튼 두 개(다시 선택·해제)를 화면 아래에 펼쳤는데, 250px 열에서는 그 두 줄이
     * 다른 지급 버튼들과 섞여 "지금 누를 수 있는 것"이 흐려졌다. 자주 하는 일이 아니라 모달로 뺀다.
     */
    introEdit() {
        const num  = Util.int($('input[name=mem_introducer]').val());
        const name = this.p.introLabel(num);
        const id   = 'member-intro-edit-modal';

        $j.Ui.JBModal.html(id, `
            <div style="padding:4px 2px">
                <p style="margin:0 0 12px;font-size:13px">현재 소개기사 <a href="#" class="js-intro-open" data-mb="${num}"><b>${name}</b></a></p>
                <div style="display:flex;gap:6px">
                    <button type="button" class="me-btn" data-intro="give">소개점수 지급</button>
                    <button type="button" class="me-btn" data-intro="pick">소개자 수정</button>
                    <button type="button" class="me-btn me-btn--danger" data-intro="clear">해제</button>
                </div>
                <p style="margin:12px 0 0;font-size:11.5px;color:#868e96;line-height:1.6">
                    바꾸거나 해제해도 <b>이미 지급한 포인트는 회수되지 않습니다.</b><br>
                    연결은 하단 [저장]을 눌러야 반영됩니다.
                </p>
            </div>`, '소개기사', { width: '380px' });

        $(`#${id}`).off('click.intro').on('click.intro', '[data-intro]', (e) => {
            const act = $(e.currentTarget).data('intro');
            $j.Ui.JBModal.close(id);
            if (act === 'give') { this.givePoint(1500, 31, num, this.p.mb.num, `${name} 소개 보상`); return; }
            if (act === 'pick') { this.findIntroducer(); return; }

            $('input[name=mem_introducer]').val('');
            this.p.introText = '';
            this.p.drawIntro();
        });
    }

    /** 추가소속 편집 모달. 선택 결과는 전역 updateSosoks() 로 돌아온다 (F/Admin/Member/Sosok.js 계약) */
    editSosok() {
        if ($('#me_mem_permit').val() === '소속자') {
            $j.F.Admin.Util.tsalert('error', '소속자는 소속변경할수 없습니다.');
            return;
        }
        $j.Ui.JBModal.target('sosok-modal', {
            target: '$j.F.Admin.Member.Sosok',
            title:  '소속',
            width:  '460px',
        });
    }

    // ───────────────────────────────────────────────── 장비

    /**
     * 기사 / 장비 관리 모달 ($j.Ui.JBModal).
     *
     * 마크업을 **열 때 만든다.** 예전에는 셸(edit-v2.blade.php)에 숨겨두고 jquery-modal 로 열었는데,
     * 같은 id 가 v1 셸에도 있어서 어느 노드를 잡는지 화면마다 갈렸다 (상담내역 모달과 같은 사정).
     *
     * ⚠️ 초기화는 **모달을 연 뒤**에 한다 — CarDrivers 가 `#cdModal` 을 찾는다.
     *
     * 좌(목록) · 우(본문) 두 칸이다. 오른쪽은 **한 번에 하나**만 보여준다 — 칩 셋(연결관리·
     * 변경이력·가입비내역)이거나 왼쪽에서 고른 대상의 편집폼. 모달을 겹겹이 띄우던 것을
     * 이 한 칸으로 모았다.
     */
    carList() {
        if (this.p.editMode === 'new') {
            $j.F.Admin.Util.tsalert('error', '회원가입후에 장비를 설정할수있습니다.');
            return;
        }

        // 마크업·초기화는 CarDrivers.open() 이 맡는다 — 기사관리 목록의 액션 메뉴와 같은 자리다
        $j.F.Admin.Member.CarDrivers.open();
    }

    /**
     * 장비 추가 — 기사 / 장비 관리 모달의 새 장비 화면을 **이 기사 앞으로** 연다.
     *
     * 예전엔 여기서 작은 모달을 따로 띄웠다. 그 모달은 한도가 찼을 때 거절만 해서, 한도를 올리고
     * 추가등록비를 빼러 다른 화면을 돌아야 했다 — 장비가 느는 길을 한 화면으로 모은다.
     * 이미 장비가 있는 기사면 새로 만들지 않고 그 장비의 정보 화면이 열린다(CarDrivers.initialize).
     */
    carAdd() {
        if (this.p.editMode === 'new') {
            $j.F.Admin.Util.tsalert('error', '회원가입후에 차량을 설정할수있습니다.');
            return;
        }
        $j.F.Admin.Member.CarDrivers.open({ view: 'carnew', mbNo: this.mbNo });
    }

    /**
     * 차고지 주소 검색 — 관리자 화면은 정보가 빼곡해서 전체화면 대신 가운데 패널로 띄운다.
     * 지도 페이지의 뒤로가기(‹)는 모바일 헤더의 일부라 PC 에서 닫기 버튼으로 읽히지 않는다.
     * 패널 자체에 × 를 얹어 어디를 눌러야 닫히는지 분명하게 한다.
     */
    findParkAddress() {
        $j.F.KakaoMap.open($('input[name=gps]').val(), '0', '', { panel: true, closeBtn: true, src: '/adm/map' });
    }

    // ───────────────────────────────────────────────── 정산

    /** 발주개수 설정 모달 */
    baljooSet() {
        if (isNaN(parseInt(this.mbNo))) {
            $j.F.Admin.Util.tsalert('error', '회원 고유번호가 없거나 잘못된정보 입니다.');
            return;
        }
        $j.Ui.JBModal.target('baljoo-cnt-modal', {
            target: '$j.F.Admin.Member.BaljooCntForm',
            data:   { mbNo: this.mbNo },
            title:  '발주 개수 설정',
            width:  '500px',
        });
    }

    /** 회비결제방식 창 — 충전금 · CMS · 장기선결제 · 장기감면 · 면제를 한 곳에서 보고 바꾼다 (장기선결제 추가는 그 안에서) */
    feeSet() {
        $j.Ui.JBModal.target('fee-way-modal', {
            target: '$j.F.Admin.Member.FeeWay',
            data:   { mbNo: this.mbNo },
            title:  '회비결제방식',
            width:  '560px',
        });
    }

    /** 회비 내역 — 달마다 방식 · 청구액 · 납부 · 변경사항. 장기선결제 목록 · 환불은 회비결제방식 창(⚙)의 [내역 · 환불] */
    feeLog() {
        $j.Ui.JBModal.target('fee-history-modal', {
            target: '$j.F.Admin.Member.FeeHistory',
            data:   { mbNo: this.mbNo },
            title:  '회비 내역',
            width:  '760px',
        });
    }

    /** 캐시 입출금 (별창) */
    cashInout() {
        const pos = centeredWindowOpts(700, 500, 'yes', 'yes');
        const win = window.open(`/adm/member/cash/inout?dom=no&dirverNum=${this.mbNo}`, 'cash_inout', pos[2]);
        if (win) { win.moveTo(pos[0], pos[1]); win.focus(); }
    }

    /** 캐시 재계산 */
    cashRecalc()  {
        this._recalc('#meCashRecalc',  '/adm/ajax/cash/recalc',
            (d) => $('#meCashLog').text(Number(d.cash ?? 0).toLocaleString()));
    }

    /** 포인트 재계산 — 캐시와 같은 성격(원장 누적잔액 ↔ 보유액 맞추기)이라 같은 흐름을 탄다 */
    pointRecalc() {
        this._recalc('#mePointRecalc', '/adm/ajax/point/recalc',
            (d) => $('#mePointVal').text(Number(d.point ?? 0).toLocaleString()));
    }

    /**
     * 원장 재계산 공용.
     *
     * 전체 새로고침은 하지 않는다 — 이 화면은 편집폼이라 저장 안 한 입력이 통째로 날아간다.
     * 대신 응답에 실린 재계산 후 값을 `render` 로 받아 화면 숫자만 핀셋으로 갈아끼운다.
     * @param {(data: object) => void} render 성공 응답으로 화면 표시값을 갱신하는 콜백
     */
    _recalc(sel, url, render) {
        if (this._recalcBusy) return;   // 두 버튼이 한 플래그를 공유한다 (동시에 돌릴 일이 없다)
        this._recalcBusy = true;

        const $btn = $(sel);
        const html = $btn.html();
        $btn.html('처리중...');

        const done = () => { $btn.html(html); this._recalcBusy = false; };
        $.post(url, { mb_no: this.mbNo })
            .done((d) => { done(); render(d); Util.swalert(['처리했습니다', 'success']); })
            .fail(() => { done(); Util.swalert('처리에 실패했습니다.'); });
    }

    /** 페이엣 아이디 등록 */
    payatReg() {
        const m = this.mb;
        if (String(Util.nvl(m.co_ceoname)).length < 2 || String(Util.nvl(m.mem_phone)).length < 6
            || String(Util.nvl(m.co_number)).length < 10 || String(Util.nvl(m.co_address)).length < 5) {
            $j.F.Admin.Util.tsalert('error', '카드 결제 신청시 사업장 주소, 대표명, 전화번호, 사업자번호가 있어야 등록됩니다.');
            return;
        }
        const id = String($('#me_payatid').val() || '');
        if (id.length < 4)          { $j.F.Admin.Util.tsalert('error', '페이앳에 신청한 사용자 아이디를 4자 이상 입력해 주세요'); return; }
        if (/['"]/.test(id))        { $j.F.Admin.Util.tsalert('error', '아이디에 특수문자등을 포함 할 수 없습니다.');            return; }
        if (!(this.mbNo > 0))       { $j.F.Admin.Util.tsalert('error', '회원 번호가 없습니다.');                                 return; }

        $.post(`/adm/ajax/member/${this.mbNo}/payat-id`, { payatid: id }, (d) => {
            if (!d.result) { $j.F.Admin.Util.tsalert('error', d.message); return; }
            $j.F.Admin.Util.tsalert('success', d.message);
            location.reload();
        });
    }

    /** 1회성 포인트 지급 */
    // label — 버튼엔 '페이앳'만 적으므로 확인창이 무엇을 몇 점 주는지 말한다
    givePoint(togoPoint, typenum, mbNo, chmemno, label = '') {
        const msg = label ? `${label} ${togoPoint}점을 지급하시겠습니까?` : '포인트를 지급하시겠습니까?';
        Util.swalert([msg, 'question'], () => {
            $.post(`/adm/ajax/member/${mbNo}/point`, { togoPoint, typenum, chmemno }, (d) => {
                // 서버가 중복지급을 막으면 사유를 그대로 보여준다 (예전엔 결과와 무관하게 새로고침해서
                // 막힌 건지 지급된 건지 화면만 봐서는 알 수 없었다)
                if (d && d.result === false) { $j.F.Admin.Util.tsalert('error', d.message || '지급하지 못했습니다.'); return; }
                location.reload();
            });
        });
    }

    /** 계좌 입력 (출금/CMS) — 출금계좌는 출금대기중이면 막는다 */
    bankEdit(n) {
        // 표시부(#meBank1/2)를 빠르게 두 번 클릭하면 ajax 왕복 중 팝업이 두 개 겹쳐 뜬다 —
        // #i_bank/#i_account 가 중복 id 가 되어 저장 시 엉뚱한(먼저 뜬) 창의 값이 실릴 수 있어 막는다.
        if (this._bankBusy) return;
        this._bankBusy = true;
        const done = () => { this._bankBusy = false; };

        if (n === 1) {
            $.getJSON('/adm/ajax/account/exist', { mb_no: this.mbNo }, (d) => {
                if (d.result === true) { $j.F.Admin.Util.tsalert('error', '출금대기중에는 계좌변경 불가합니다.'); done(); return; }
                this._bankPrompt(n).finally(done);
            }).fail(done);
        } else {
            this._bankPrompt(n).finally(done);
        }
    }

    async _bankPrompt(n) {
        const arr = String(Util.nvl(this.mb[`co_bank${n}`])).split('|');
        // 예금주 칸이 비어있으면(=신규 등록) 본인인증 실명을 기본값으로 채운다.
        // 실명이 없으면 mem_name 말미의 동명이인 구분용 영문·숫자만 떼고 쓴다 (member_naming/emp_naming 산물).
        const holderDefault = Util.nvl(arr[2]) || this.mb.real_name
            || String(Util.nvl(this.mb.mem_name)).replace(/[A-Za-z0-9]+$/, '').trim();

        // 은행명 칸 — 목록에 없는 기존 값(레거시 데이터)도 입력칸에 그대로 보존된다.
        let bankField = null;
        // 칸이 셋이라 html 슬롯에 직접 짜고 onConfirm 에서 읽는다 (구 Swal preConfirm 자리)
        let r = null;
        await $j.Ui.Dialog.open({
            title: n === 2 ? 'CMS계좌 입력' : '출금계좌 입력',
            html: `<div id="i_bank_field"></div>
                   <input id="i_account" class="ui-dlg-input" placeholder="계좌번호" value="${Util.nvl(arr[1])}">
                   <div class="ui-dlg-input-clear">
                       <input id="i_actholder" class="ui-dlg-input" placeholder="예금주" value="${holderDefault}">
                       <button type="button" id="i_actholder_clear" class="ui-dlg-input-clear__btn" aria-label="예금주 지우기">✕</button>
                   </div>`,
            confirm: '확인',
            cancel:  '취소',
            onOpen: () => {
                bankField = $j.Ui.BankField.mount('#i_bank_field', { items: this.p.banks, logoSprite: this.p.bankLogoSprite, value: Util.nvl(arr[0]) });
                // 본인인증 실명이 기본값으로 깔려 있어, 실제 예금주가 다르면 지우고 새로 쳐야 한다.
                $('#i_actholder_clear').on('click', () => {
                    $('#i_actholder').val('').trigger('focus');
                });
            },
            onConfirm: () => {
                r = {
                    bank:      bankField.value,
                    account:   $('#i_account').val(),
                    actholder: $('#i_actholder').val(),
                };
            },
        });
        bankField.close();   // 목록을 펼친 채 ✕ 로 닫으면 패널이 남는다
        if (!r) return;

        const str = (r.bank || r.account || r.actholder) ? encodeMemberBank(r.bank, r.account, r.actholder) : '';
        this.mb[`co_bank${n}`] = str;
        this._bankStage(n, str);                                     // 저장에 실리는 값
        $(`#meBank${n}`).text(str.replaceAll('|', ' , '));           // 화면 표시
    }

    /**
     * 계좌 hidden 을 전송대상으로 올린다.
     *
     * hidden 은 disabled 로 그려져 있어 평소엔 POST 에 실리지 않는다 — 그래야 서버가
     * 저장 시점의 회사 행에서 값을 채워, 그 사이 회원이 앱에서 등록한 계좌를 덮지 않는다.
     * CS 가 실제로 계좌를 고친 이 순간에만 잠금을 풀어 "바꾼 것만 저장"이 되게 한다.
     */
    _bankStage(n, str) {
        $(`input[name=co_bank${n}]`).val(str).prop('disabled', false);
    }

    /** 계좌 복사 (출금↔CMS) */
    bankCopy(dest) {
        const src = dest === 1 ? 2 : 1;
        const str = Util.nvl(this.mb[`co_bank${src}`]);
        if (!str) return;

        this.mb[`co_bank${dest}`] = str;
        this._bankStage(dest, str);
        $(`#meBank${dest}`).text(str.replaceAll('|', ' , '));
        $j.F.Admin.Util.tsalert('info', dest === 1 ? 'CMS→출금 계좌복사' : '출금→CMS 계좌복사');
    }

    /** 가상계좌 발급 */
    assignVirtualAccount() {
        if ($('#meBank3').text().trim()) return;   // 이미 있으면 무시
        Util.swalert('가상계좌를 발급하시겠습니까?', () => {
            $.post('/adm/ajax/account/assignBulk', { mb_no: this.mbNo }, () => {
                $j.F.Admin.Util.tsalert('info', '가상계좌가 발급되었습니다.');
                setTimeout(() => location.reload(), 1500);
            });
        }, ['예', '아니오']);
    }

    /** CMS 자동이체 신청서 — MEMBER 정보 + CMS계좌(co_bank2)로 양식 자동 채움 */
    cmsForm() {
        const m  = this.mb;
        const b2 = Util.decodeMemberBank(Util.nvl(m.co_bank2));
        const coNo = Util.nvl(m.co_number);
        $j.Ui.JBModal.target('cms-form-modal', {
            target: '$j.F.Admin.Member.CmsForm',
            data: {
                customerNo:    Util.nvl(m.cms_num),
                applicantName: Util.nvl(m.mem_name),                            // 동명이인 식별용 접미(A·1 등) 포함 이름 그대로
                phone:         Util.nvl(m.mem_phone2),
                mobile:        Util.nvl(m.mem_phone),
                email:         Util.nvl(m.mem_email),
                address:       Util.nvl(m.co_address),
                bank:          Util.nvl(b2.bank),
                account:       Util.nvl(b2.account),
                holderName:    Util.nvl(b2.actholder),
                rrn:           Util.nvl(m.mem_birthday),
                bizNo:         coNo ? Util.coNumber(coNo) : '',
                holderType:    '개인',
                carType:       Util.nvl(m.mem_car_type),
            },
            title:    'CMS 자동이체 신청서',
            width:    '560px',
            position: 'right',
        });
    }

    // ─────────────────────────────────── 누락 정보 (배치를 바꾸지 않고 얹은 것)

    /**
     * -6차 지급·사용 내역 모달.
     * `/adm/ajax/member/{n}/msix` API 와 `$j.F.Admin.Member.Msix` 모달이 이미 있는데
     * v1 기사편집창에서는 어디에도 연결돼 있지 않았다. 발주개수 행의 배지에서 연다.
     */
    msixList() {
        $j.Ui.JBModal.target('member-msix-modal', {
            target: '$j.F.Admin.Member.Msix',
            data:   { mbNo: this.mbNo, memName: this.mb.mem_name },
            title:  '-6차 내역',
            width:  '950px',   // 9열(오더번호~상태) 표라 620px 에서는 셀마다 줄바꿈이 났다
        });
    }

    /**
     * 발주개수 변경내역 모달 — 자원 탭 '발주개수'의 현황을 누르면 열린다.
     * 발주개수는 소유자 귀속이라 서버가 mem_ownercode 원장을 읽어준다.
     */
    odrcntLog() {
        this._resModal('member-odrcnt-modal', '발주개수 내역', `/adm/ajax/member/${this.mbNo}/odrcnt`, (d) => {
            const o = d.owner || {};
            // 건수·쪽수는 아래 페이저가 말한다 — 위아래에서 같은 말을 하지 않는다
            const head = `보유 <b>${this._n(o.mem_order_cnt)}</b>개 · 차수 <b>${this._n(o.mem_ordercounter)}</b>차`;
            const rows = (d.list || []).map((r) => `<tr>
                <td>${r.date}</td>
                <td>${this._esc(r.label)}${r.order ? `<br><span style="color:#868e96">${this._esc(r.order)}</span>` : ''}</td>
                <td class="text-right" style="color:${r.diff < 0 ? '#c92a2a' : '#1864ab'};font-weight:700">${r.diff > 0 ? '+' : ''}${this._n(r.diff)}</td>
                <td class="text-right">${this._n(r.total)}</td>
            </tr>`).join('');
            return { head, rows, cols: ['일시', '사유', '증감', '잔여'], span: 4 };
        });
    }

    /**
     * 보유캐시 내역 모달 — 열2 '보유캐시'의 금액을 누르면 열린다 (수주포인트와 같은 규약).
     *
     * **새 거래를 만드는 일은 여전히 옆 [입출금] 별창이 한다.** 여기서 되는 것은 그 별창에서
     * 잘못 찍은 행의 **정정**뿐이다 — 금액·메모를 고치거나 행을 지운다. 어디까지 손대도 되는지는
     * 서버(CashEditService)가 정해 각 행의 `lock` 으로 내려준다. 빈 값이면 [수정] 버튼이 붙고,
     * 아니면 잠긴 이유를 그 자리에 적는다 (버튼만 감추면 "왜 이 줄만 안 되냐"를 다시 묻는다).
     *
     * 캐시는 소유자 귀속이라 서버가 mem_ownercode 원장(g5_cash)을 읽어준다.
     */
    cashLog() {
        this._resModal('member-cash-modal', '보유캐시 내역', `/adm/ajax/member/${this.mbNo}/cash`, (d) => {
            // '가용'은 보류(hold)를 뺀 값이라 표 맨 윗줄의 누적잔액과 다를 수 있다 → 말로 구분해 둔다
            const head = `가용 <b>${this._n(d.have)}</b>원`;
            // 정정 권한이 없는 관리자에게는 '관리' 칸 자체를 안 그린다 (서버가 canEdit 으로 말해준다)
            const can  = !!d.canEdit;
            const cols = ['일시', '사유', '오더번호', '증감', '누적잔액'];
            if (can) cols.push('');

            const rows = (d.list || []).map((r) => `<tr data-num="${r.num}" data-amount="${r.amount}" data-memo="${this._esc(r.memo)}" data-label="${this._esc(r.label)}">
                <td>${r.date}</td>
                <td>${this._esc(r.label)}${r.memo ? `<br><span style="color:#868e96">${this._esc(r.memo)}</span>` : ''}</td>
                <td>${r.relTable === 'order' && r.relId ? r.relId : ''}</td>
                <td class="text-right" style="color:${r.amount < 0 ? '#c92a2a' : '#1864ab'};font-weight:700">${r.amount > 0 ? '+' : ''}${this._n(r.amount)}</td>
                <td class="text-right">${this._n(r.total)}</td>
                ${can ? `<td class="text-center" style="white-space:nowrap">${r.lock
                    ? `<i class="fas fa-lock" style="color:#ced4da;cursor:help" data-tip-hover data-tip="${this._esc(r.lock)}"></i>`
                    : '<button type="button" class="me-btn me-btn--icon js-cash-edit" data-tip-hover data-tip="이 내역을 정정합니다"><i class="fas fa-pen"></i></button>'}</td>` : ''}
            </tr>`).join('');
            return { head, rows, cols, span: cols.length };
        }, { width: '860px', bind: ($body, reload) => this._bindCashEdit($body, reload) });
    }

    /**
     * 보유캐시 내역의 행 정정 — 누른 행 **아래에** 편집줄을 펼친다.
     *
     * 행을 폼으로 갈아끼우지 않는 이유: 원본 금액이 화면에서 사라지면 '무엇을 무엇으로 고치는지'가
     * 안 보인다. 위에 원본, 아래에 고칠 값이 같이 있어야 저장 전에 눈으로 대조한다.
     *
     * 이벤트는 $body 위임이다 — 목록은 저장할 때마다 통째로 다시 그려진다.
     */
    _bindCashEdit($body, reload) {
        const row = (el) => $(el).closest('tr.js-cash-editor').prev();

        $body.on('click', '.js-cash-edit', (e) => {
            const $tr = $(e.currentTarget).closest('tr');
            $body.find('tr.js-cash-editor').remove();

            const amount = Util.int($tr.data('amount'));
            const memo   = String($tr.data('memo') ?? '');
            $tr.after(`<tr class="js-cash-editor"><td colspan="${$tr.children().length}" style="background:var(--adm-surface-soft,#f8f9fa);padding:8px 10px">
                <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">
                    <select class="me-input js-ce-io" style="width:74px;flex:0 0 74px">
                        <option value="입금"${amount >= 0 ? ' selected' : ''}>입금</option>
                        <option value="출금"${amount < 0 ? ' selected' : ''}>출금</option>
                    </select>
                    <input type="text" class="me-input js-ce-amt" inputmode="numeric" data-amount-kor style="width:110px;flex:0 0 110px;text-align:right"
                           value="${Math.abs(amount).toLocaleString()}">
                    <input type="text" class="me-input js-ce-memo" style="flex:1;min-width:150px" maxlength="150"
                           value="${this._esc(memo)}" placeholder="메모">
                    <button type="button" class="me-btn js-ce-save">저장</button>
                    <button type="button" class="me-btn js-ce-cancel">취소</button>
                    <button type="button" class="me-btn me-btn--icon js-ce-del" style="margin-left:12px;color:#c92a2a"
                            data-tip-hover data-tip="이 내역을 원장에서 지웁니다"><i class="fas fa-trash"></i></button>
                </div>
                <div style="margin-top:6px;color:#868e96">사유를 바꾸려면 이 줄을 지우고 [입출금] 창에서 다시 넣으세요. 정정하면 이 아래 내역의 누적잔액이 모두 다시 계산됩니다.</div>
            </td></tr>`);
        });

        $body.on('keyup input', '.js-ce-amt', (e) => $j.Common.Form.formatNumberInput(e.target));
        $body.on('click', '.js-ce-cancel', (e) => $(e.currentTarget).closest('tr.js-cash-editor').remove());

        $body.on('click', '.js-ce-save', (e) => {
            const $ed  = $(e.currentTarget).closest('tr.js-cash-editor');
            const $tr  = row(e.currentTarget);
            const io   = String($ed.find('.js-ce-io').val());
            const amt  = Util.int(String($ed.find('.js-ce-amt').val()).replace(/[^0-9]/g, ''));
            const memo = String($ed.find('.js-ce-memo').val() || '');
            const before = Util.int($tr.data('amount'));
            const after  = io === '출금' ? -amt : amt;

            if (before === after && memo === String($tr.data('memo') ?? '')) {
                $j.F.Admin.Util.tsalert('error', '바뀐 내용이 없습니다.');
                return;
            }
            if (!confirm(`이 내역을 정정합니다.\n\n${this._sn(before)} → ${this._sn(after)}\n\n이후 내역의 누적잔액이 모두 다시 계산됩니다. 진행할까요?`)) return;

            this._cashPost(`/adm/ajax/member/cash/${Util.int($tr.data('num'))}/edit`,
                { inorout: io, amountof: amt, memos: memo }, reload, '정정했습니다.');
        });

        $body.on('click', '.js-ce-del', (e) => {
            const $tr = row(e.currentTarget);
            if (!confirm(`이 내역을 삭제합니다.\n\n${$tr.data('label')} ${this._sn(Util.int($tr.data('amount')))}원\n\n되돌릴 수 없습니다. 진행할까요?`)) return;

            this._cashPost(`/adm/ajax/member/cash/${Util.int($tr.data('num'))}/delete`, {}, reload, '삭제했습니다.');
        });
    }

    /** 캐시 정정 공용 POST — 모달을 다시 그리고 편집창 열2의 보유캐시도 같이 갈아끼운다 */
    _cashPost(url, data, reload, okMsg) {
        $.ajax({ type: 'POST', url, data, dataType: 'json' })
            .done((d) => {
                if (!d.result) {
                    $j.F.Admin.Util.tsalert('error', d.message || '처리하지 못했습니다.');
                    return;
                }
                $j.F.Admin.Util.tsalert('success', okMsg);
                // 모달만 고치면 뒤 화면의 보유캐시가 옛 값으로 남는다 — 재계산 버튼과 같은 자리를 쓴다
                if (d.cash !== undefined) $('#meCashLog').text(Number(d.cash).toLocaleString());
                reload();
            })
            .fail((xhr) => $j.F.Admin.Util.tsalert('error', `요청 중 오류가 발생했습니다. (${xhr.status})`));
    }

    /**
     * 수주포인트 내역 모달 — 자원 탭 '수주포인트'의 현황을 누르면 열린다.
     *
     * **목록만** 보여준다. 지급(소개기사·페이앳·스티커)은 탭 안(화면)에서 하고 여기서는 하지 않는다 —
     * '내역을 보는 창'과 '값을 바꾸는 자리'가 섞여 있으면 무엇을 하러 연 창인지 흐려진다.
     * 대신 오더번호로 좁혀볼 수 있다. 최근 100건 밖의 건도 찾아야 해서 필터는 서버가 건다.
     */
    pointLog() {
        this._resModal('member-point-modal', '수주포인트 내역', `/adm/ajax/member/${this.mbNo}/points`, (d) => {
            const q = Util.int(d.ordernum);
            // 건수·쪽수는 아래 페이저가 말한다. 여기서는 '지금 무엇으로 좁혀져 있는지'만 알린다
            const head = `보유 <b>${this._n(d.have)}</b>P`
                       + (q ? ` <span style="color:#1864ab">· 오더 ${q} 로 좁힘</span>` : '');
            const rows = (d.list || []).map((r) => `<tr>
                <td>${r.date}</td>
                <td>${r.relTable === 'order' && r.relId
                    // 누르면 그 오더의 내역으로 거른다 (모바일 수주포인트 화면과 같은 동작) — 풀기는 [전체보기]
                    ? `<a href="javascript:void(0)" class="js-res-filter" data-q="${Util.int(r.relId)}" title="이 오더만 보기">${Util.int(r.relId)}</a>`
                    : ''}</td>
                <td>${this._esc(r.label)}${r.memo ? `<br><span style="color:#868e96">${this._esc(r.memo)}</span>` : ''}</td>
                <td class="text-right" style="color:${r.point < 0 ? '#c92a2a' : '#2b8a3e'};font-weight:700">${r.point > 0 ? '+' : ''}${this._n(r.point)}</td>
                <td class="text-right">${this._n(r.total)}</td>
            </tr>`).join('');
            return { head, rows, cols: ['일시', '오더번호', '사유', '증감', '누적'], span: 5 };
        }, { width: '780px', search: { param: 'ordernum', placeholder: '오더번호' } });
    }

    /**
     * 자원 내역 모달 공용 — 열고, 받아오고, 표로 그린다.
     * 내용은 모달 본문(`.ui-modal__body`)에만 넣는다 — `#id` 는 헤더·닫기버튼까지 품은 바깥 껍데기다.
     *
     * @param {object} [opts] width: 모달 폭 · search: {param, placeholder} 면 검색줄을 얹는다.
     *                        bind: ($body, reload) 면 모달마다 자기 이벤트를 더 건다 (reload 는 지금 쪽 다시 읽기).
     *                        검색은 본문을 다시 그리므로 이벤트는 $body 위임으로 건다(매 렌더마다 살아남게).
     */
    _resModal(id, title, url, build, opts = {}) {
        $j.Ui.JBModal.html(id, '<div style="padding:20px;text-align:center;color:#888">로딩중...</div>',
            title, { width: opts.width || '560px' });
        const $body = $(`#${id}`).find('.ui-modal__body');

        // 현재 요청 파라미터. 페이저가 검색어를 물고 넘어가야 해서 마지막 값을 들고 있는다
        // (page 만 갈아끼운다 — 검색 중에 2쪽을 누르면 검색이 풀리면 안 된다).
        let params = {};

        const searchRow = (q) => !opts.search ? '' : `
            <div class="me-search">
                <input type="text" class="me-input js-res-q" style="width:110px;flex:0 0 110px"
                       value="${this._esc(q || '')}" placeholder="${opts.search.placeholder}" autocomplete="off">
                <button type="button" class="me-btn js-res-go">검색</button>
                ${q ? '<button type="button" class="me-btn js-res-clear">전체보기</button>' : ''}
            </div>`;

        const load = (next = {}) => {
            params = next;
            $.getJSON(url, params)
                .done((d) => {
                    const v = build(d);
                    $body.html(`
                        ${searchRow(opts.search && params[opts.search.param])}
                        <div style="padding:2px 2px 10px;font-size:12px">${v.head}</div>
                        ${v.extra || ''}
                        <table class="table table-bordered" style="width:100%;font-size:11.5px">
                            <thead><tr>${v.cols.map((c) => `<th>${c}</th>`).join('')}</tr></thead>
                            <tbody>${v.rows || `<tr><td colspan="${v.span}" style="text-align:center;color:#adb5bd;padding:14px">내역이 없습니다.</td></tr>`}</tbody>
                        </table>
                        ${$j.F.Admin.Util.pager(d)}`);
                })
                .fail(() => {
                    $body.html('<div style="padding:20px;text-align:center;color:#c92a2a">데이터를 불러오지 못했습니다.</div>');
                });
        };

        // 이벤트는 $body 위임 — 본문을 통째로 다시 그리므로 버튼에 직접 걸면 두 번째 렌더부터 죽는다.
        $body.on('click', '.me-pg__btn', (e) => load({ ...params, page: Util.int($(e.currentTarget).data('page')) }));

        if (opts.search) {
            // 검색·전체보기는 page 를 넘기지 않는다 = 항상 1쪽부터 (조건이 바뀌면 쪽수도 바뀐다)
            const go = () => {
                const q = String($body.find('.js-res-q').val() || '').trim();
                load(q ? { [opts.search.param]: q } : {});
            };
            $body.on('click', '.js-res-go', go)
                 .on('click', '.js-res-clear', () => load({}))
                 // 표 안의 값(오더번호 등)을 누르면 그 값으로 검색 — 검색칸에 친 것과 같다
                 .on('click', '.js-res-filter', (e) => load({ [opts.search.param]: String($(e.currentTarget).data('q')) }))
                 .on('keydown', '.js-res-q', (e) => { if (e.key === 'Enter') { e.preventDefault(); go(); } });
        }

        // 쓰기를 가진 모달용 — 저장 후 '보던 쪽'으로 돌아와야 한다 (page 를 버리면 1쪽으로 튄다)
        if (opts.bind) opts.bind($body, () => load(params));

        load();
    }

    _n(v)   { return Number(v ?? 0).toLocaleString(); }
    /** 부호를 살린 금액 — 확인창에서 '무엇을 무엇으로' 를 말할 때 쓴다 */
    _sn(v)  { return (v > 0 ? '+' : '') + Number(v ?? 0).toLocaleString(); }
    _esc(s) {
        return String(s ?? '').replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[c]));
    }

    // ───────────────────────────────────────────────── 진단

    /** 푸시 / 수주 테스트 모달 */
    pushTest(mbNo = this.mbNo, name = null) {
        if (!mbNo || isNaN(parseInt(mbNo))) {
            $j.F.Admin.Util.tsalert('error', '회원 고유번호가 없거나 잘못된정보 입니다.');
            return;
        }
        const id = 'jb-modal-push-test';
        $j.Ui.JBModal.html(id, `
            <div style="padding:20px;text-align:center">
                <p style="margin-bottom:20px;font-size:14px">${this._esc(Util.nvl(name || this.mb.mem_name, '이'))} 회원에게 어떤 테스트를 발송할까요?</p>
                <div style="display:flex;justify-content:center;gap:10px;flex-wrap:wrap">
                    <button type="button" class="btn btn-primary" data-act="push" data-type="register" style="width:140px;height:38px">접수알림 발송</button>
                    <button type="button" class="btn btn-success" data-act="push" data-type="match"    style="width:140px;height:38px">매칭알림 발송</button>
                    <button type="button" class="btn btn-warning" data-act="order" style="width:140px;height:38px">수주테스트 발주</button>
                </div>
                <div id="push-test-status" style="margin-top:18px;color:#888;font-size:13px;min-height:18px"></div>
            </div>`, '푸시 / 수주 테스트', { width: '460px' });

        $(`#${id}`).off('click.pt')
            .on('click.pt', '[data-act="push"]',  (e) => this._post(`/adm/ajax/member/${mbNo}/push-test`, { type: $(e.currentTarget).data('type') }))
            .on('click.pt', '[data-act="order"]', () => {
                if (!confirm('이 회원에게 수주 테스트 발주를 보냅니다.\n(내일 오전 9시 · 차고지 기준 · 전달사항: ㅡ 수주테스트 ㅡ)\n진행할까요?')) return;
                this._post(`/adm/ajax/member/${this.mbNo}/order-test`, {});
            });
    }

    /** 푸시테스트 모달 공용 POST — 결과를 모달 안에도 남긴다 */
    _post(url, data) {
        const $s = $('#push-test-status');
        $s.text('처리 중...');
        $.ajax({ type: 'POST', url, data, dataType: 'json' })
            .done((d) => {
                const msg = d.message || (d.result ? '완료' : '실패');
                $j.F.Admin.Util.tsalert(d.result ? 'success' : 'error', msg);
                $s.text(msg);
            })
            .fail((xhr) => {
                $j.F.Admin.Util.tsalert('error', `요청 중 오류가 발생했습니다. (${xhr.status})`);
                $s.text('요청 실패');
            });
    }

    /** 변경로그 — 회원 팝오버와 같은 창 (F/Admin/Member/Logs) */
    memberLogs(mbNo = this.mbNo) {
        $j.F.Admin.Member.Logs.changes(mbNo);
    }

    /** 로그인 기록 — 회원 팝오버와 같은 창 (F/Admin/Member/Logs) */
    loginLogs(mbNo = this.mbNo) {
        $j.F.Admin.Member.Logs.logins(mbNo);
    }

    /**
     * 앱로그수집 — 앱 환경 상태(스냅샷)와 수집된 로그를 한 화면에.
     *
     * 위에서 아래가 상담원의 판단 순서다. 상태 요약을 먼저 보고, 거기서 답이 안 나오면 로그를 요청해
     * 들어온 로그를 열어본다. 요약의 근거인 판정 표·원본 스냅샷은 맨 아래에 둔다.
     *   사이트 칩 → 상태 요약 → 로그 요청 → 수집된 로그 표 → 상태 판정 표
     *
     * 로그요청은 회원 화면에 아무것도 띄우지 않는다 — 팝업·TTS·효과음이 전부 꺼진
     * 커맨드 푸시(`diag>logreq>회원`)로 앱만 조용히 깨운다. "종료했다 켜주세요"를
     * 대신하는 자리다. 수집 경로는 이것 하나다.
     *
     * 앱은 맨 위 칩으로 고른다. 앱마다 푸시토큰·로그가 따로라(서버 app_code · 로그 패키지명)
     * 한 번에 한 앱만 본다. 기본은 서버 기본값(JB). 고를 수 있는 앱(apps)과 그 앱이 환경 스냅샷을
     * 보내는지(snapshot)는 서버 응답이 정한다 — 이 화면은 앱 코드를 알지 않는다.
     *
     * 뼈대는 첫 응답에서 한 번만 그리고, 앱 전환·새로고침·로그 대기 때는 **칸 안의 내용만** 바꾼다.
     * 본문을 통째로 다시 그리면 모달이 '로딩중' 한 줄 높이로 줄었다 늘며 깜빡인다.
     */
    appDiag(mbNo = this.mbNo) {
        const ID   = 'member-app-diag-modal';
        const LOADING = '<div style="padding:20px;text-align:center;color:#888">로딩중...</div>';
        const FAILED  = '<div style="padding:20px;text-align:center;color:#c92a2a">데이터를 불러오지 못했습니다.</div>';
        const TONE = { 3: ['danger', '치명'], 2: ['warn', '경고'], 1: ['info', '안내'], 0: ['ok', '정상'] };
        const POLL_MS = 5000;
        const POLL_MAX = 36;   // 5초 × 36 = 3분. 앱이 죽어 있으면 아무리 기다려도 안 온다

        $j.Ui.JBModal.html(ID, LOADING, '앱로그수집', { width: '680px' });
        const $body = $(`#${ID}`).find('.ui-modal__body');
        const esc = (v) => this._esc(v == null ? '' : v);

        // 폴링 상태 — 모달 하나당 하나. 닫으면 반드시 멈춘다(아래 EventBus).
        const poll = { timer: null, left: 0, baseNum: null };
        let app = '';        // 보는 앱 — 서버 app_code. 비워 보내면 서버 기본 앱이고, 응답의 app_code 로 맞춘다
        let data = null;
        let built = false;   // 뼈대를 그렸나 — 한 번만 그린다

        const showPoll = () => {
            if (poll.timer) $body.find('.js-poll-msg').text(`로그 대기중… ${poll.left * 5}초 남음`);
            $body.find('.js-poll-stop').toggle(!!poll.timer);
        };

        const stopPoll = (msg) => {
            if (poll.timer) { clearInterval(poll.timer); poll.timer = null; }
            poll.left = 0;
            if (msg !== undefined) $body.find('.js-poll-msg').text(msg);
            $body.find('.js-poll-stop').hide();
        };

        // 모달이 닫히면 타이머를 끈다. 안 끄면 화면을 떠난 뒤에도 5초마다 서버를 때린다.
        $j.Common.EventBus.on('memberAppDiag', 'JBModal:close', (e, d) => {
            if (d && d.id === ID) { stopPoll(); $j.Common.EventBus.off('memberAppDiag'); }
        });

        const markApp = () => {
            $body.find('.js-app').each((i, el) => $(el).toggleClass('is-on', String($(el).data('app')) === app));
        };

        // 칸 채우기 — 뼈대는 그대로 두고 내용만 바꾼다
        const fill = () => {
            if (!built) { $body.html(this._appDiagSkeleton(data.apps, esc)); built = true; }
            markApp();
            $body.find('.js-diag-summary').html(this._appDiagSummary(data, TONE, esc));
            $body.find('.js-log-section').html(this._appDiagLogs(data, esc));
            $body.find('.js-diag-findings').html(this._appDiagFindings(data, TONE, esc));
            $body.find('.js-diag-live').css('opacity', '');
            showPoll();
        };

        const load = (then) => {
            // 응답이 오기 전에 앱을 바꿨으면 버린다 — 늦게 온 응답이 다른 앱 화면을 덮는다.
            const want = app;
            $.getJSON(`/adm/ajax/member/${mbNo}/app-diag`, want ? { app_code: want } : {})
                .done((d) => { if (want !== app) return; data = d; if (d.app_code) app = d.app_code; fill(); if (then) then(d); })
                .fail(() => {
                    if (want !== app) return;
                    if (!built) { $body.html(FAILED); return; }
                    $body.find('.js-diag-live').css('opacity', '');
                    $body.find('.js-diag-summary').html(FAILED);
                });
        };

        const startPoll = () => {
            stopPoll();
            // 지금 최신 로그 번호를 기준선으로 잡는다. 이보다 큰 번호가 생기면 새 로그다.
            poll.baseNum = (data && data.logs && data.logs[0]) ? Util.int(data.logs[0].num) : 0;
            poll.left = POLL_MAX;
            poll.timer = setInterval(() => {
                // 모달이 DOM 에서 사라졌으면(다른 경로로 닫힘) 즉시 정지 — 타이머 누수 방지
                if (!$.contains(document, $body[0])) { stopPoll(); return; }

                poll.left -= 1;
                if (poll.left <= 0) { stopPoll('3분 동안 로그가 오지 않았습니다 — 앱이 죽어 있을 수 있습니다.'); return; }

                load((d) => {
                    const top = (d.logs && d.logs[0]) ? Util.int(d.logs[0].num) : 0;
                    if (top > poll.baseNum) {
                        stopPoll('');
                        $j.F.Admin.Util.tsalert('success', '새 로그가 도착했습니다.');
                    }
                });
            }, POLL_MS);
            showPoll();
        };

        // 이벤트는 $body 위임 — 뼈대를 첫 응답 뒤에 그리므로 버튼에 직접 걸 수 없다.
        $body.off('click.appdiag').on('click.appdiag', '.js-app', (e) => {
            const next = String($(e.currentTarget).data('app'));
            if (next === app) return;
            // 폴링 기준선은 보던 앱의 최신 로그 번호다 — 앱이 바뀌면 그 기준이 틀리므로 멈춘다.
            stopPoll('');
            app = next;
            markApp();
            $body.find('.js-diag-live').css('opacity', .45);   // 칸만 흐리게 — 뼈대·높이는 그대로 둔다
            load();
        }).on('click.appdiag', '.js-logreq', () => {
            const sentApp = app;
            $body.find('.js-logreq').prop('disabled', true);
            $.post(`/adm/ajax/member/${mbNo}/app-diag/logreq`, { app_code: sentApp }, (r) => {
                $body.find('.js-logreq').prop('disabled', false);
                if (!r.result) { $j.F.Admin.Util.tsalert('error', r.message); return; }
                $j.F.Admin.Util.tsalert('success', r.message);
                // 보냈으니 들어오는지 지켜본다. 그 사이 앱을 바꿨으면 지켜볼 화면이 없다.
                if (sentApp === app) startPoll();
            }).fail(() => {
                $body.find('.js-logreq').prop('disabled', false);
                $j.F.Admin.Util.tsalert('error', '발송에 실패했습니다.');
            });
        }).on('click.appdiag', '.js-poll-stop', () => stopPoll('자동 확인을 멈췄습니다.'))
          .on('click.appdiag', '.js-reload', () => load())
          .on('click.appdiag', '.js-log-dl', (e) => this._downloadLog($(e.currentTarget).data()));

        load();
    }

    /** 앱로그수집 칸 제목 */
    _appDiagCaption(text) {
        return `<div style="font-size:12px;font-weight:700;color:var(--adm-text-heading, #212529);margin:16px 0 6px">${text}</div>`;
    }

    /**
     * 앱로그수집 뼈대 — 한 번만 그린다. 내용이 바뀌는 칸은 js-diag-summary · js-log-section · js-diag-findings.
     * 사이트 칩은 서버가 준 목록 그대로이고, 고를 앱이 하나뿐이면 그리지 않는다.
     */
    _appDiagSkeleton(apps, esc) {
        const list = Array.isArray(apps) ? apps : [];
        const chips = list.length < 2 ? '' : `
            <div class="me-chips">
                ${list.map((a) => `<button type="button" class="me-chip js-app" data-app="${esc(a.code)}">${esc(a.name)}</button>`).join('')}
            </div>`;

        return `${chips}
            <div class="js-diag-live" style="transition:opacity .15s">
                ${this._appDiagCaption('앱 환경 상태')}
                <div class="js-diag-summary"></div>

                ${this._appDiagCaption('로그 요청')}
                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                    <button type="button" class="me-btn js-logreq"
                            style="background:#e7f5ff;border-color:#a5d8ff;color:#1864ab;font-weight:700">
                        로그 요청 보내기
                    </button>
                    <button type="button" class="me-btn js-reload" style="padding:0 10px"
                            title="새로고침" aria-label="새로고침"><i class="fas fa-sync-alt"></i></button>
                    <span class="js-poll-msg" style="font-size:11.5px;color:#1864ab"></span>
                    <button type="button" class="me-btn js-poll-stop" style="display:none">자동 확인 멈추기</button>
                </div>
                <div style="font-size:11.5px;color:#868e96;line-height:1.7;margin-top:6px">
                    요청을 보내면 회원 화면에는 <b>아무것도 뜨지 않고</b> 앱만 깨어나 로그를 올립니다
                    (팝업·소리·읽어주기 모두 꺼진 커맨드 푸시).
                    <span style="color:#e67700">앱이 이 커맨드를 처리하는 버전부터 동작합니다.</span>
                </div>

                <div class="js-log-section"></div>
                <div class="js-diag-findings"></div>
            </div>`;
    }

    /** 수집된 로그 칸 — 로그가 없으면 제목·표 모두 그리지 않는다 */
    _appDiagLogs(d, esc) {
        const logs = d.logs || [];
        if (!logs.length) return '';

        const rows = logs.map((g) => `<tr>
            <td style="white-space:nowrap">${esc(String(g.regdate || '').substr(2, 14))}</td>
            <td>${esc(g.type || '')}</td>
            <td>${esc(g.ver || '')}</td>
            <td><button type="button" class="me-btn js-log-dl"
                        data-url="${esc(g.url || '')}"
                        data-regdate="${esc(g.regdate || '')}"
                        data-type="${esc(g.type || '')}">다운로드</button></td>
        </tr>`).join('');

        return `
            ${this._appDiagCaption('수집된 로그')}
            <table class="table table-bordered" style="width:100%;font-size:11.5px;margin-bottom:0">
                <thead><tr><th style="width:110px">일시</th><th style="width:60px">분류</th><th style="width:60px">앱버전</th><th style="width:78px">받기</th></tr></thead>
                <tbody>${rows}</tbody>
            </table>`;
    }

    /**
     * 로그 파일 내려받기 — `/adm/app/logs` 목록과 같은 방식(fetch → blob → a[download]).
     *
     * 파일이 이미지 서버(다른 도메인)에 있어 링크만 걸면 브라우저가 열어버린다. blob 으로
     * 받아야 '저장'이 된다. 대신 CORS 가 막히면 fetch 가 실패하므로 그때는 새 창으로 연다.
     *
     * 파일명은 목록의 `mb_no(날짜)` 규약에 시각·분류를 덧붙였다 — 이 모달은 한 회원의
     * 같은 날 로그를 여러 건 늘어놓으므로, 날짜까지만 쓰면 받는 파일 이름이 전부 같아진다.
     */
    _downloadLog(d) {
        const url = String(d.url || '');
        if (!url) return;

        const t = String(d.regdate || '').replace(/[^0-9]/g, ''); // 20260903115102
        const name = `${this.mbNo}(${t.substr(2, 2)}-${t.substr(4, 2)}-${t.substr(6, 2)}`
                   + ` ${t.substr(8, 4)} ${d.type || ''})`.trim() + '.txt';

        fetch(url)
            .then((res) => res.blob())
            .then((blob) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
            })
            .catch(() => window.open(url, '_blank'));
    }

    /**
     * 앱 환경 상태 요약 — 맨 위 칸. 앱에 아무것도 요청하지 않고 서버에 이미 있는 값만 본다.
     * 스냅샷이 없으면 왜 없는지를 말한다('없음'이 세 가지고 상담원이 할 일이 다르다).
     */
    _appDiagSummary(d, TONE, esc) {
        if (!d.has) {
            // 스냅샷을 보내는 통로 자체가 없는 앱(d.snapshot=false)도 있다 — '구버전이라 안 온다'고 말하면 업데이트를 안내하게 된다.
            const appName = ((d.apps || []).find((a) => a.code === d.app_code) || {}).name || '이 앱';
            const msg = !d.hasDevice
                ? ['등록된 푸시토큰이 없습니다.',
                   '이 상태로는 상태값 이전에 푸시 자체가 갈 수 없습니다 — 앱 재로그인이 필요합니다.']
                : d.snapshot === false
                ? [`${appName}은(는) 앱 환경 상태를 보내지 않습니다.`,
                   '아래에서 로그 요청을 보내 확인하세요.']
                : ['아직 수집된 환경 스냅샷이 없습니다.',
                   '앱이 보내면 자동으로 올라옵니다. 구버전 앱은 보내지 않습니다.'];
            return `<div style="padding:10px 12px;font-size:12px;line-height:1.7;color:#868e96;
                                background:var(--adm-surface-sunken, #f1f3f5);border-radius:var(--adm-radius-sm, 4px)">
                ${d.hasDevice ? '' : '<span class="me-badge me-badge--danger">치명</span> '}
                <b style="color:var(--adm-text, #495057)">${esc(msg[0])}</b> ${esc(msg[1])}
            </div>`;
        }

        const [tone, label] = TONE[Util.int(d.level)] || TONE[0];
        const dev = d.device || {};
        return `
            <div style="font-size:12px;line-height:1.8">
                <span class="me-badge me-badge--${tone}" style="font-size:12px">${label}</span>
                <span style="margin-left:8px">수집 <b>${esc(String(d.recv || '').substr(0, 16))}</b></span><br>
                <span style="color:#868e96">
                    ${esc(dev.device_label || ((dev.device_co || '') + ' ' + (dev.device_name || '')))}
                    · OS ${esc(dev.device_os || '?')} · 앱 ${esc(dev.mobie_ver || '?')}
                </span>
            </div>`;
    }

    /**
     * 앱 환경 상태 판정 표·원본 스냅샷 — 맨 아래 칸(요약의 근거).
     * 스냅샷이 없으면 아무것도 그리지 않는다 — 이유는 맨 위 요약이 말한다.
     * 판정이 없으면(이상 없음) 제목·표는 그리지 않고 원본 스냅샷만 남긴다 — '정상'은 맨 위 배지가 말한다.
     */
    _appDiagFindings(d, TONE, esc) {
        if (!d.has) return '';

        const findings = d.findings || [];
        const rows = findings.map((f) => {
            const [t, l] = TONE[Util.int(f.level)] || TONE[0];
            return `<tr>
                <td style="white-space:nowrap"><span class="me-badge me-badge--${t}">${l}</span></td>
                <td>${esc(f.label)}</td>
                <td style="color:#868e96">${esc(f.action)}</td>
            </tr>`;
        }).join('');

        const table = !findings.length ? '' : `
            ${this._appDiagCaption('앱 환경 상태 판정')}
            <table class="table table-bordered" style="width:100%;font-size:11.5px;margin-bottom:0">
                <thead><tr><th style="width:56px">등급</th><th>판정</th><th style="width:38%">조치</th></tr></thead>
                <tbody>${rows}</tbody>
            </table>`;

        return `${table}
            <details style="margin-top:14px">
                <summary style="cursor:pointer;font-size:12px;color:#868e96">원본 스냅샷</summary>
                <pre style="max-height:240px;overflow:auto;background:#f8f9fa;border:1px solid #dee2e6;
                            padding:10px;font-size:11px;margin-top:6px">${esc(JSON.stringify(d.snap || {}, null, 2))}</pre>
            </details>`;
    }

    /** 중복 로그인 리셋 (푸시토큰 초기화) */
    /**
     * 약관 동의 내역 모달 — 열1 '위치동의'·'마케팅동의' 라벨에서 연다.
     * select 는 현재 상태만 말한다. 언제·어느 버전에·어떤 경로로 동의했는지는 여기서 본다.
     */
    termsLog() {
        const TRIGGER = {
            JOIN: '가입', SERVICE_JOIN: '서비스가입', LOCATION_GATE: '위치게이트',
            ADMIN_PROXY: '관리자 대리', SETTING_TOGGLE: '설정변경', MARKETING_POPUP: '마케팅팝업', AD_SMS_OPTOUT: '080 수신거부',
            ORDER_COMPLETE: '작업완료', RE_CONSENT: '재동의', REPORT_SUBMIT: '악덕 제보등록',
            LEGACY_ASSUME: '소급간주',
        };
        const RESULT = { 1: ['동의', '#2b8a3e'], 2: ['거부', '#c92a2a'], 3: ['철회', '#e67700'] };

        this._resModal('member-terms-modal', '약관 동의 내역', `/adm/ajax/member/${this.mbNo}/terms-consents`, (d) => {
            const rows = (d.list || []).map((r) => {
                const [label, color] = RESULT[Util.int(r.consent_result)] || ['?', '#868e96'];
                return `<tr>
                    <td>${this._esc(String(r.consented_at || '').substr(0, 16))}</td>
                    <td>${this._esc(r.name || r.code || '(삭제된 약관)')}
                        ${r.version ? `<br><span style="color:#868e96">v${Util.int(r.version_no)} · ${this._esc(r.version)}</span>` : ''}
                        ${Util.int(r.hash_diff) ? '<br><span style="color:#c92a2a">본문 변경됨</span>' : ''}</td>
                    <td style="color:${color};font-weight:700">${label}</td>
                    <td>${this._esc(TRIGGER[r.trigger_type] || r.trigger_type || '')}</td>
                    <td>${this._esc(r.device_type || '')}<br><span style="color:#868e96">${this._esc(r.ip_address || '')}</span></td>
                </tr>`;
            }).join('');
            return {
                head: `전체 <b>${this._n(d.total)}</b>건 · 최신순`,
                rows, cols: ['일시', '약관', '결과', '경로', '기기'], span: 5,
            };
        }, { width: '720px' });
    }

    // 하단 액션바의 '중복체크'(차량번호+사업자번호 동시검사)는 없앴다 (2026-08).
    // 두 검사 모두 입력칸 blur 로 그 자리에서 돈다 — checkCoNumber 는 #me_co_number,
    // 차량번호는 기사 / 장비 관리 모달의 새 장비 화면이 한다 (CarDrivers.checkPlate).

    // ───────────────────────────────────────────────── 조회 / 위험

    docs(mbNo = this.mbNo)  { $j.F.Admin.Util.userDocs(mbNo); }

    /** 기사탈퇴 */
    leave() {
        if (!confirm('이 회원을 탈퇴시킬까요?\n탈퇴되면 검색이나 기타 모든 조건에서 제외됩니다.')) return;
        $.ajax({ type: 'DELETE', url: `/adm/ajax/member/${this.mbNo}` }).done(() => {
            alert('회원을 탈퇴시켰습니다.');
            location.reload();
        });
    }

    /** 탈퇴/대기 복구 · 휴면 해제 — 셋 다 같은 엔드포인트(rollback). 서버가 상태를 보고 가른다 */
    recover() {
        const st = Util.int(this.mb.mem_status);
        const isDormant = st === 2;   // 휴면 (MemberDormantService::STATUS)
        const msg = st === 99 ? '이 회원 탈퇴를 취소할까요?'
            : isDormant ? '이 회원의 휴면을 해제할까요?\n해제되면 앱을 다시 켤 때부터 바로 쓸 수 있습니다.'
            : '이 회원의 대기상태를 취소할까요?';
        if (!confirm(msg)) return;
        $.post(`/adm/ajax/member/${this.mbNo}/rollback`, {}, () => {
            alert(isDormant ? '휴면을 해제했습니다.' : '회원을 복구 시켰습니다.');
            location.replace(`/adm/member/edit?mb_no=${this.mbNo}`);
        });
    }

    /** 소유자 → 다른 소유자의 소속자로 이전 (수발주자 전용) */
    transfer() {
        $j.Ui.JBModal.target('transfer-modal', {
            target: '$j.F.Admin.Member.TransferForm',
            data:   { mbNo: this.mbNo, mbName: this.mb.mem_name },
            title:  '소속자로 이전',
            width:  '520px',
        });
    }

    /**
     * 소속자 추가 — 기사 / 장비 관리 모달의 추가 화면을 연다.
     *
     * 예전엔 이 창을 추가 모드(`?act=add`)로 바꿨다. 그 화면은 소속자만 만들고 끝나서
     * 한도·추가등록비·장비 등록이 따로 놀았다 — 소속자를 넣는 길을 모달 하나로 모은다.
     * 창을 떠나지 않으므로 고치던 내용도 그대로다.
     */
    addChild() {
        $j.F.Admin.Member.CarDrivers.open({ view: 'add' });
    }
}
